import { ApplicationModuleCode } from '@/domain/application-modules/application-module.interface';
import { ApplicationModulesService } from '@/domain/application-modules/application-modules.service';
import { FeatureCode } from '@/utils/disabled-features/disabled-features.interface';
import { DisabledFeaturesService } from '@/utils/disabled-features/disabled-features.service';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import Intercom, { shutdown, update } from '@intercom/messenger-js-sdk';
import { combineLatest, firstValueFrom, type Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import type { UserCore } from '../interfaces/User.interface';
import { RosaAuthService } from '../libs/rosa';
import { BrandingProvider } from '../providers/branding/branding.provider';
import { ProfileProvider } from '../providers/profile/profile.provider';
import { ProjectsProvider } from '../providers/projects/projects.provider';

@Injectable({
    providedIn: 'root'
})
export class TutorialService {
    private _projectsProvider = inject(ProjectsProvider);
    private _profileProvider = inject(ProfileProvider);
    private _rosaAuthService = inject(RosaAuthService);
    private _disabledFeaturesProvider = inject(DisabledFeaturesService);
    private _brandingProvider = inject(BrandingProvider);
    private _applicationModulesProvider = inject(ApplicationModulesService);

    isIntercomEnabled$ = signal(false);

    async initTutorial() {
        // Intercom
        combineLatest([this._rosaAuthService.areGeneralConditionAccepted$(), this._brandingProvider.hasBrandingAccessToTutorial$()])
            .pipe(
                filter(([areGeneralConditionAccepted, hasBrandingAccessToTutorial]) => {
                    if (
                        this._disabledFeaturesProvider.isFeatureEnabled(FeatureCode.INTERCOM) &&
                        !this._applicationModulesProvider.hasModuleAccessR(ApplicationModuleCode.HIDE_TO_ITALY) && //TODO : it's not perfect for hiding intercom, we want a method to hide intercom for specific project
                        areGeneralConditionAccepted &&
                        hasBrandingAccessToTutorial
                    ) {
                        return true;
                    } else {
                        this.disableIntercomHelper();
                        return false;
                    }
                }),
                switchMap(([_, __]) => combineLatest([this._profileProvider.getDefinedUser$(), this._rosaAuthService.isConnectedAs$()])),
                tap(([currentUser, isConnectAs]) =>
                    currentUser && !isConnectAs ? this.enableIntercomHelper() : this.disableIntercomHelper()
                ),
                filter(([_, isConnectAs]) => !isConnectAs),
                map(([currentUser, _]) => currentUser),
                filter(isNotNullish),
                shareReplay({ refCount: true, bufferSize: 1 })
            )
            .subscribe(currentUser => {
                this.setIntercomAccount(currentUser);
            });
    }

    public isTutorialEnabled$(): Observable<boolean> {
        return toObservable(this.isIntercomEnabled$);
    }

    private async setIntercomAccount(user: UserCore) {
        const projectId = await firstValueFrom(this._projectsProvider.getUserCurrentProjectId$());
        const isCustomBranding = !(await firstValueFrom(this._brandingProvider.isNotCustomBranding$()));
        const userRolesCodes = (await this._profileProvider.getUserProfileRoles$()).map(profileRole => profileRole.roleCode);
        const userLanguage = await firstValueFrom(this._profileProvider.getUserLanguage$());

        update({
            user_id: user.id,
            name: user.firstname ?? undefined,
            email: user.email,
            currentProjectId: projectId,
            hasAccessToDataboardFeature: userRolesCodes.includes('databoards'),
            hasAccessToAlertFeature: userRolesCodes.includes('alert_role'),
            language_override: userLanguage,
            isProjectAdmin: userRolesCodes.includes('project_admin'),
            customBranding: isCustomBranding
            /*
                /!\ /!\ /!\ NE PAS METTRE LE NUMÉRO DE TÉLÉPHONE / DO NOT SET THE PHONE NUMBER /!\ /!\ /!\

                For RGPD reasons and because intercom store its data in an US server,
                we shall never set the phone number of the client on the data we give to intercom

                For more info, ask Clément B.
            */
        });
    }

    private disableIntercomHelper() {
        console.info('Intercom :: disabled');
        this.isIntercomEnabled$.set(false);
        shutdown();
    }

    private enableIntercomHelper() {
        console.info('Intercom :: enabled');
        this.isIntercomEnabled$.set(true);
        Intercom({ app_id: 'chsc0o00', region: 'eu' });
    }
}
